// src/components/modules/PhilosophyModuleDetail.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getToolsForModule } from '../../data/moduleTools';
import { getPointsForPlacement } from '../../data/positionPoints';
import PositionPrompt from './PositionPrompt';
import TableCard from './TableCard';
import {
  ChevronLeft,
  Clock,
  CheckCircle,
  ChevronRight,
  Target,
  Brain,
  BookOpen,
  Award,
  Lightbulb,
  Users,
  MessageSquare,
  ArrowRight,
  Play,
  Pause,
  RotateCcw,
  FileText,
  Beaker,
  TrendingUp,
  AlertCircle,
  Sparkles,
  Heart,
  Coffee,
  Eye,
  PartyPopper,
  Microscope,
  Zap,
  Feather,
  User,
  Map
} from 'lucide-react';
import Card from '../shared/Card';
import Button from '../shared/Button';
import { LINEAGE } from '../../data/lineageMap';
import { getThinkerProfile } from '../../data/thinkerProfiles';
import { useApp } from '../../context/AppContext';
import CompletionCertificate from './CompletionCertificate';
import CourseMasteryCertificate from './CourseMasteryCertificate';
import { useGamificationStore } from '../../stores/gamificationStore';
import { getAllModules } from '../../data/modules';

// Celebration Component
const CelebrationModal = ({ show, onClose, message = "Section Complete" }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 1000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          exit={{ scale: 0, rotate: 180 }}
          transition={{ type: "spring", duration: 0.5 }}
          role="status"
          className="bg-gradient-to-br from-green-600 to-emerald-700 p-8 rounded-2xl shadow-2xl"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex items-center justify-center mb-4"
          >
            <CheckCircle className="w-20 h-20 text-white" strokeWidth={2.5} />
          </motion.div>
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-2">{message}</h2>
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ repeat: Infinity, duration: 1 }}
            className="text-center"
          >
            <Sparkles className="w-8 h-8 text-yellow-300 mx-auto" />
          </motion.div>
        </motion.div>

        {/* Confetti particles */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            initial={{
              x: "50vw",
              y: "50vh",
              opacity: 1,
              scale: 1
            }}
            animate={{
              x: `${Math.random() * 100}vw`,
              y: `${Math.random() * 100}vh`,
              opacity: 0,
              scale: 0,
              rotate: Math.random() * 360
            }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="absolute w-3 h-3 rounded-full"
            style={{
              backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#95E1D3', '#F38181'][i % 5]
            }}
          />
        ))}
      </motion.div>
    </AnimatePresence>
  );
};

// Markdown parsing helper with special formatting blocks
const parseMarkdownText = (text) => {
  if (!text || typeof text !== 'string') return text;

  // Split text into lines first to handle headings and lists
  const lines = text.split('\n');
  const elements = [];
  let currentParagraph = [];

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      elements.push({ type: 'paragraph', content: currentParagraph.join('\n'), key: `p-${elements.length}` });
      currentParagraph = [];
    }
  };

  lines.forEach((line, idx) => {
    const trimmedLine = line.trim();

    // Handle headings
    if (trimmedLine.startsWith('###')) {
      flushParagraph();
      elements.push({ type: 'h3', content: trimmedLine.replace(/^###\s*/, ''), key: `h3-${idx}` });
    } else if (trimmedLine.startsWith('##')) {
      flushParagraph();
      elements.push({ type: 'h2', content: trimmedLine.replace(/^##\s*/, ''), key: `h2-${idx}` });
    } else if (trimmedLine.startsWith('**') && trimmedLine.includes('**')) {
      // Check if this is a standalone subheading or inline bold with content after
      const closingMarkerIndex = trimmedLine.indexOf('**', 2);
      if (closingMarkerIndex !== -1) {
        const afterBold = trimmedLine.slice(closingMarkerIndex + 2).trim();
        // Check if afterBold is just parenthetical subtitle text like "(Big vs. Small)" or just punctuation/colon
        const isJustSubtitle = afterBold.match(/^[\s:;\-—–]*(\([^)]+\))?[\s:;\-—–]*$/);

        // If there's substantial content after the bold part (not just subtitle or punctuation),
        // treat it as a regular paragraph with inline bold formatting
        if (afterBold.length > 2 && !isJustSubtitle && afterBold.replace(/^[:;\-—–\s]+/, '').length > 0) {
          // This is inline bold with content - treat as regular paragraph
          currentParagraph.push(line);
        } else {
          // This is a standalone subheading (possibly with subtitle in parentheses)
          flushParagraph();
          let content = trimmedLine.slice(2);
          const closingIndex = content.indexOf('**');
          if (closingIndex !== -1) {
            content = content.slice(0, closingIndex) + content.slice(closingIndex + 2);
          }
          elements.push({ type: 'subheading', content: content, key: `sub-${idx}` });
        }
      } else {
        // No closing **, treat as regular paragraph
        currentParagraph.push(line);
      }
    } else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
      // List items
      flushParagraph();
      elements.push({ type: 'li', content: trimmedLine.slice(2), key: `li-${idx}` });
    } else if (trimmedLine === '') {
      // Empty line - flush current paragraph
      flushParagraph();
    } else {
      // Regular text - add to current paragraph
      currentParagraph.push(line);
    }
  });

  flushParagraph(); // Flush any remaining paragraph

  // Now process each element for special blocks
  const processedElements = [];

  elements.forEach((element) => {
    if (element.type === 'paragraph') {
      // Process paragraph for special blocks
      const specialBlockRegex = /\[([A-Z]+):([^\]]+)\]/g;
      const parts = [];
      let lastIndex = 0;
      let match;

      while ((match = specialBlockRegex.exec(element.content)) !== null) {
        // Add text before the special block
        if (match.index > lastIndex) {
          parts.push({ type: 'text', content: element.content.slice(lastIndex, match.index), key: `${element.key}-text-${lastIndex}` });
        }

        // Add the special block
        const [fullMatch, blockType, fullContent] = match;

        // Parse the content based on block type
        let parsedBlock = {
          type: blockType.toLowerCase(),
          key: `${element.key}-block-${match.index}`
        };

        if (blockType === 'COMPARE') {
          // COMPARE blocks have format: [COMPARE:weakTitle|weakContent|strongTitle|strongContent]
          const pipes = [];
          for (let i = 0; i < fullContent.length; i++) {
            if (fullContent[i] === '|') {
              pipes.push(i);
              if (pipes.length === 3) break;
            }
          }

          if (pipes.length === 3) {
            parsedBlock.weakTitle = fullContent.slice(0, pipes[0]).trim();
            parsedBlock.weakContent = fullContent.slice(pipes[0] + 1, pipes[1]).trim();
            parsedBlock.strongTitle = fullContent.slice(pipes[1] + 1, pipes[2]).trim();
            parsedBlock.strongContent = fullContent.slice(pipes[2] + 1).trim();
          } else {
            const pipeIndex = fullContent.indexOf('|');
            if (pipeIndex !== -1) {
              parsedBlock.title = fullContent.slice(0, pipeIndex).trim();
              parsedBlock.content = fullContent.slice(pipeIndex + 1).trim();
            } else {
              parsedBlock.content = fullContent.trim();
            }
          }
        } else {
          // STAT and QUOTE blocks have format: [TYPE:title|content]
          const pipeIndex = fullContent.indexOf('|');
          if (pipeIndex !== -1) {
            parsedBlock.title = fullContent.slice(0, pipeIndex).trim();
            parsedBlock.content = fullContent.slice(pipeIndex + 1).trim();
          } else {
            parsedBlock.content = fullContent.trim();
          }
        }

        parts.push(parsedBlock);
        lastIndex = match.index + fullMatch.length;
      }

      // Add remaining text
      if (lastIndex < element.content.length) {
        parts.push({ type: 'text', content: element.content.slice(lastIndex), key: `${element.key}-text-${lastIndex}` });
      }

      // If no special blocks found, treat as simple text
      if (parts.length === 0) {
        parts.push({ type: 'text', content: element.content, key: `${element.key}-text-all` });
      }

      // Add all paragraph parts to processed elements
      processedElements.push(...parts);
    } else {
      // Non-paragraph elements added directly
      processedElements.push(element);
    }
  });

  // Render all processed elements
  return processedElements.map((part) => {
    // Handle headings with colorful styling
    if (part.type === 'h2') {
      return (
        <h2 key={part.key} className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-conversation-teal to-cyan-400 mt-8 mb-4">
          {parseSimpleMarkdown(part.content)}
        </h2>
      );
    }

    if (part.type === 'h3') {
      return (
        <h3 key={part.key} className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-bright-gold to-amber-400 mt-6 mb-3">
          {parseSimpleMarkdown(part.content)}
        </h3>
      );
    }

    if (part.type === 'subheading') {
      return (
        <div key={part.key} className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mt-5 mb-3">
          {parseSimpleMarkdown(part.content)}
        </div>
      );
    }

    if (part.type === 'li') {
      return (
        <div key={part.key} className="flex items-start gap-3 mb-2 ml-4">
          <div className="w-2 h-2 rounded-full bg-gradient-to-r from-conversation-teal to-bright-gold mt-2 flex-shrink-0"></div>
          <div className="text-slate-300">{parseSimpleMarkdown(part.content)}</div>
        </div>
      );
    }

    // Special blocks below
    if (part.type === 'stat') {
      return (
        <div key={part.key} className="my-6 p-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border-l-4 border-cyan-500 rounded-r-lg">
          <div className="flex items-start gap-3">
            <TrendingUp className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold text-cyan-300 text-sm uppercase tracking-wider mb-1">{part.title}</div>
              <div className="text-slate-200 leading-relaxed">{parseSimpleMarkdown(part.content)}</div>
            </div>
          </div>
        </div>
      );
    }

    if (part.type === 'quote') {
      return (
        <div key={part.key} className="my-6 p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-l-4 border-purple-500 rounded-r-lg">
          <div className="flex items-start gap-3">
            <MessageSquare className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold text-purple-300 text-sm mb-1">{part.title}</div>
              <div className="text-slate-200 italic leading-relaxed">"{parseSimpleMarkdown(part.content)}"</div>
            </div>
          </div>
        </div>
      );
    }

    if (part.type === 'research') {
      return (
        <div key={part.key} className="my-6 p-4 bg-gradient-to-r from-indigo-500/10 to-blue-500/10 border-l-4 border-indigo-500 rounded-r-lg">
          <div className="flex items-start gap-3">
            <Microscope className="w-6 h-6 text-indigo-400 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold text-indigo-300 text-sm uppercase tracking-wider mb-1">{part.title}</div>
              <div className="text-slate-200 leading-relaxed">{parseSimpleMarkdown(part.content)}</div>
            </div>
          </div>
        </div>
      );
    }

    if (part.type === 'observation') {
      return (
        <div key={part.key} className="my-6 p-4 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border-l-4 border-amber-500 rounded-r-lg">
          <div className="flex items-start gap-3">
            <Eye className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold text-amber-300 text-sm uppercase tracking-wider mb-1">{part.title}</div>
              <div className="text-slate-200 leading-relaxed">{parseSimpleMarkdown(part.content)}</div>
            </div>
          </div>
        </div>
      );
    }

    if (part.type === 'insight') {
      return (
        <div key={part.key} className="my-6 p-4 bg-gradient-to-r from-violet-500/10 to-purple-500/10 border-l-4 border-violet-500 rounded-r-lg">
          <div className="flex items-start gap-3">
            <Zap className="w-6 h-6 text-violet-400 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold text-violet-300 text-sm uppercase tracking-wider mb-1">{part.title}</div>
              <div className="text-slate-200 leading-relaxed">{parseSimpleMarkdown(part.content)}</div>
            </div>
          </div>
        </div>
      );
    }

    if (part.type === 'try') {
      return (
        <div key={part.key} className="my-6 p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-l-4 border-amber-500 rounded-r-lg">
          <div className="flex items-start gap-3">
            <Beaker className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold text-amber-300 text-sm uppercase tracking-wider mb-1">Try this now{part.title ? `: ${part.title}` : ''}</div>
              <div className="text-slate-200 leading-relaxed">{parseSimpleMarkdown(part.content)}</div>
            </div>
          </div>
        </div>
      );
    }

    if (part.type === 'anchor') {
      return (
        <div key={part.key} className="my-6 p-4 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-l-4 border-emerald-500 rounded-r-lg">
          <div className="flex items-start gap-3">
            <Coffee className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold text-emerald-300 text-sm uppercase tracking-wider mb-1">You've already met this{part.title ? `: ${part.title}` : ''}</div>
              <div className="text-slate-200 leading-relaxed">{parseSimpleMarkdown(part.content)}</div>
            </div>
          </div>
        </div>
      );
    }

    if (part.type === 'pause') {
      return (
        <div key={part.key} className="my-10 px-6 py-8 bg-slate-800/40 border border-slate-600/40 rounded-xl text-center">
          <Feather className="w-5 h-5 text-indigo-300 mx-auto mb-3" />
          <div className="text-indigo-300 text-xs uppercase tracking-widest mb-3">Before you read on</div>
          <div className="text-slate-200 leading-relaxed italic max-w-xl mx-auto">{parseSimpleMarkdown(part.content)}</div>
        </div>
      );
    }

    if (part.type === 'compare') {
      // New format with explicit fields
      if (part.weakTitle && part.strongTitle) {
        return (
          <div key={part.key} className="my-6 grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="font-bold text-red-400 text-sm mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {part.weakTitle}
              </div>
              <div className="text-slate-300 text-sm">{parseSimpleMarkdown(part.weakContent)}</div>
            </div>
            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <div className="font-bold text-green-400 text-sm mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                {part.strongTitle}
              </div>
              <div className="text-slate-300 text-sm">{parseSimpleMarkdown(part.strongContent)}</div>
            </div>
          </div>
        );
      }

      // Fallback for old format
      const [weak, strong] = (part.content || '').split('|').map(s => s.trim());
      return (
        <div key={part.key} className="my-6 grid md:grid-cols-2 gap-4">
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <div className="font-bold text-red-400 text-sm mb-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {part.title?.split('|')[0] || 'Weak'}
            </div>
            <div className="text-slate-300 text-sm">{parseSimpleMarkdown(weak)}</div>
          </div>
          <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
            <div className="font-bold text-green-400 text-sm mb-2 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              {part.title?.split('|')[1] || 'Strong'}
            </div>
            <div className="text-slate-300 text-sm">{parseSimpleMarkdown(strong)}</div>
          </div>
        </div>
      );
    }

    // Regular text with bold/italic
    return <span key={part.key}>{parseSimpleMarkdown(part.content)}</span>;
  });
};

const ITALIC_CLASS = "italic text-purple-300";

// Wrap *emphasis* runs in <em>, leaving everything else as-is
const renderEmphasis = (text, keyBase) =>
  text.split(/(\*[^*]+\*)/).map((part, index) => (
    part.startsWith('*') && part.endsWith('*') && part.length > 2
      ? <em key={`${keyBase}-italic-${index}`} className={ITALIC_CLASS}>{part.slice(1, -1)}</em>
      : <span key={`${keyBase}-text-${index}`}>{part}</span>
  ));

// Helper to parse simple markdown (bold/italic) within text
const parseSimpleMarkdown = (text) => {
  if (!text || typeof text !== 'string') return text;

  // ***bold italic*** first so the greedier marker wins over **bold**
  const parts = text.split(/(\*\*\*[^*]+\*\*\*|\*\*.*?\*\*)/);

  return parts.map((part, index) => {
    if (part.startsWith('***') && part.endsWith('***') && part.length > 6) {
      return (
        <strong key={`bolditalic-${index}`} className="font-bold text-white">
          <em className={ITALIC_CLASS}>{part.slice(3, -3)}</em>
        </strong>
      );
    }

    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      // Bold can itself contain *emphasis*, so parse the inside too
      return (
        <strong key={`bold-${index}`} className="font-bold text-white">
          {renderEmphasis(part.slice(2, -2), `bold-${index}`)}
        </strong>
      );
    }

    return renderEmphasis(part, `plain-${index}`);
  });
};

const MarkdownText = ({ children, className = "" }) => {
  if (!children) return null;

  // Split by double line breaks to create paragraphs
  const paragraphs = children.split('\n\n').filter(p => p.trim());

  return (
    <div className={className}>
      {paragraphs.map((paragraph, idx) => (
        <div key={idx} className={idx > 0 ? 'mt-4' : ''}>
          {parseMarkdownText(paragraph)}
        </div>
      ))}
    </div>
  );
};

// Component for rendering sections with expandable content
// Keys SectionContent renders explicitly below; anything else in section.content
// is a named teaching block ({title, content}) that must render via the walker,
// or 83% of the authored module content silently disappears (review 2026-08-16).
const RESERVED_SECTION_KEYS = [
  'intro', 'mainContent', 'scientificFoundation', 'keyPoints',
  'deepDive', 'practicalApplication', 'commonMistakes'
];

// Sparring on invitation — a quiet handoff to Sofia at the end of every
// section page. Prefills the companion input via navigation state and never
// auto-sends: the reader finishes the sentence, so they control how hard
// she pushes (the "constant challenging" complaint is the design constraint).
// Biography on invitation — the SparPrompt philosophy applied to people. A
// section that substantially introduces one of the sixteen anchor thinkers
// carries `meetThinkers: ['id']`; the affordance is one quiet line to their
// Lineage profile, never an interruption to the argument itself. It renders
// only for thinkers whose full profile actually exists.
const MeetThinkers = ({ ids }) => {
  const thinkers = (ids || [])
    .map(id => LINEAGE.find(n => n.id === id))
    .filter(node => node && getThinkerProfile(node.id));
  if (thinkers.length === 0) return null;
  const links = thinkers.map((node, i) => (
    <React.Fragment key={node.id}>
      {i > 0 && (i === thinkers.length - 1 ? ' and ' : ', ')}
      <Link to={`/lineage/${node.id}`} className="text-conversation-teal font-semibold hover:underline">
        {/* "Meet The Buddha" reads wrong mid-sentence; lowercase a leading article */}
        {node.name.replace(/^The /, 'the ')}
      </Link>
    </React.Fragment>
  ));
  const tail = thinkers.length > 1
    ? 'the lives behind the argument'
    : thinkers[0].id === 'ubuntu' ? 'the tradition behind the argument' : 'the life behind the argument';
  return (
    <div className="mt-8 flex items-start gap-2.5 text-sm text-slate-400">
      <User className="w-4 h-4 mt-0.5 flex-shrink-0 text-conversation-teal/70" />
      <p className="min-w-0">
        Meet {links}, {tail}, on The Lineage.
      </p>
    </div>
  );
};

const SparPrompt = ({ title, module }) => (
  <div className="mt-8 pt-5 border-t border-white/10 flex items-center gap-3">
    <img
      src="/images/characters/sofia.webp"
      alt=""
      className="w-8 h-8 rounded-full border border-bright-gold/50 object-cover flex-shrink-0"
      onError={(e) => { e.target.style.display = 'none'; }}
    />
    <p className="text-sm text-slate-300 min-w-0">
      Formed a view?{' '}
      <Link
        to="/companion"
        state={{
          seed: `Take the opposite side to me on "${title}". My current view is: `,
          ...(module ? { fromModule: { id: module.id, title: module.title, section: title } } : {}),
        }}
        className="text-bright-gold font-semibold hover:underline"
      >
        Put it to Sofia
      </Link>
      {' '}and she'll argue the other side.
    </p>
  </div>
);

const SectionContent = ({ section, module }) => {
  const [expandedParts, setExpandedParts] = useState({
    scientificFoundation: false,
    deepDive: false,
    practicalApplication: false,
    commonMistakes: false
  });

  const toggleExpanded = (part) => {
    setExpandedParts(prev => ({
      ...prev,
      [part]: !prev[part]
    }));
  };

  return (
    <Card hover={false} blur={false} className="p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <h3 className="text-2xl font-bold text-white">{section.title}</h3>
          <span className="text-sm text-slate-400">({section.duration})</span>
        </div>
      </div>

      <div className="space-y-6">
        {/* Introduction */}
        <div className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 border-l-4 border-indigo-500 rounded-r-xl p-6">
          <MarkdownText className="text-slate-200 text-lg leading-relaxed first-letter:text-5xl first-letter:font-bold first-letter:text-indigo-400 first-letter:mr-3 first-letter:float-left">
            {section.content.intro}
          </MarkdownText>
        </div>

        {/* Main Content */}
        {section.content.mainContent && (
          <div>
            <MarkdownText className="text-slate-300 text-base leading-relaxed whitespace-pre-line">
              {section.content.mainContent}
            </MarkdownText>
          </div>
        )}

        {/* Named teaching blocks, rendered in authored order */}
        {Object.entries(section.content)
          .filter(([key, value]) =>
            !RESERVED_SECTION_KEYS.includes(key) &&
            value && typeof value === 'object' && !Array.isArray(value) &&
            typeof value.content === 'string'
          )
          .map(([key, block]) => (
            <div key={key}>
              {block.title && (
                <h4 className="text-xl font-bold text-white mt-2 mb-4">{block.title}</h4>
              )}
              <MarkdownText className="text-slate-300 text-base leading-relaxed whitespace-pre-line">
                {block.content}
              </MarkdownText>
              {typeof block.content2 === 'string' && (
                <MarkdownText className="text-slate-300 text-base leading-relaxed whitespace-pre-line">
                  {block.content2}
                </MarkdownText>
              )}
            </div>
          ))}

        {/* Scientific Foundation */}
        {section.content.scientificFoundation && (
          <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border-2 border-blue-400/40 rounded-xl p-6 hover:border-blue-400/60 transition-all shadow-lg shadow-blue-500/10">
            <button
              onClick={() => toggleExpanded('scientificFoundation')}
              aria-expanded={!!expandedParts.scientificFoundation}
              className="w-full text-left group"
            >
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-bold text-blue-200 flex items-center gap-3 group-hover:text-blue-100 transition-colors">
                  <Beaker className="w-6 h-6 text-blue-400" />
                  {section.content.scientificFoundation.title}
                </h4>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-blue-300 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                    {expandedParts.scientificFoundation ? 'Click to collapse' : 'Click to expand'}
                  </span>
                  <ChevronRight
                    className={`w-6 h-6 text-blue-400 transition-transform group-hover:text-blue-300 ${
                      expandedParts.scientificFoundation ? 'rotate-90' : ''
                    }`}
                  />
                </div>
              </div>
            </button>
            
            <AnimatePresence>
              {expandedParts.scientificFoundation && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="mt-4 space-y-4">
                    <MarkdownText className="text-slate-300 leading-relaxed whitespace-pre-line">
                      {section.content.scientificFoundation.content}
                    </MarkdownText>
                    
                    {/* Key Studies if present */}
                    {section.content.scientificFoundation.keyStudies && (
                      <div className="mt-6 space-y-3">
                        <h5 className="text-sm font-medium text-blue-300 uppercase tracking-wider">Key Research</h5>
                        {section.content.scientificFoundation.keyStudies.map((study, idx) => (
                          <div key={idx} className="p-3 bg-blue-900/20 rounded-lg border border-blue-500/20">
                            <div className="text-white font-medium">{study.researcher}</div>
                            <div className="text-blue-200 text-sm">{study.institution}, {study.year}</div>
                            <div className="text-slate-300 text-sm mt-1">{study.finding}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Key Points */}
        {section.content.keyPoints && section.content.keyPoints.length > 0 && (
          <div>
            <h4 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              Key Insights
            </h4>
            <div className="space-y-3">
              {section.content.keyPoints.map((point, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-purple-500/10 rounded-lg">
                  <div className="w-6 h-6 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-purple-300 text-sm font-bold">{index + 1}</span>
                  </div>
                  <MarkdownText className="text-slate-300">
                    {point}
                  </MarkdownText>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Deep Dive */}
        {section.content.deepDive && (
          <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-2 border-purple-400/40 rounded-xl p-6 hover:border-purple-400/60 transition-all shadow-lg shadow-purple-500/10">
            <button
              onClick={() => toggleExpanded('deepDive')}
              aria-expanded={!!expandedParts.deepDive}
              className="w-full text-left group"
            >
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-bold text-purple-200 flex items-center gap-3 group-hover:text-purple-100 transition-colors">
                  <Brain className="w-6 h-6 text-purple-400" />
                  {section.content.deepDive.title}
                </h4>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-purple-300 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                    {expandedParts.deepDive ? 'Click to collapse' : 'Click to expand'}
                  </span>
                  <ChevronRight
                    className={`w-6 h-6 text-purple-400 transition-transform group-hover:text-purple-300 ${
                      expandedParts.deepDive ? 'rotate-90' : ''
                    }`}
                  />
                </div>
              </div>
            </button>
            
            <AnimatePresence>
              {expandedParts.deepDive && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="mt-4">
                    <MarkdownText className="text-slate-300 leading-relaxed whitespace-pre-line">
                      {section.content.deepDive.content}
                    </MarkdownText>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Practical Application */}
        {section.content.practicalApplication && (
          <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-400/40 rounded-xl p-6 hover:border-green-400/60 transition-all shadow-lg shadow-green-500/10">
            <button
              onClick={() => toggleExpanded('practicalApplication')}
              aria-expanded={!!expandedParts.practicalApplication}
              className="w-full text-left group"
            >
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-bold text-green-200 flex items-center gap-3 group-hover:text-green-100 transition-colors">
                  <Lightbulb className="w-6 h-6 text-green-400" />
                  {section.content.practicalApplication.title}
                </h4>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-green-300 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                    {expandedParts.practicalApplication ? 'Click to collapse' : 'Click to expand'}
                  </span>
                  <ChevronRight
                    className={`w-6 h-6 text-green-400 transition-transform group-hover:text-green-300 ${
                      expandedParts.practicalApplication ? 'rotate-90' : ''
                    }`}
                  />
                </div>
              </div>
            </button>
            
            <AnimatePresence>
              {expandedParts.practicalApplication && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="mt-4 space-y-4">
                    {/* Handle simple content format */}
                    {section.content.practicalApplication.content && (
                      <MarkdownText className="text-slate-300 leading-relaxed whitespace-pre-line">
                        {section.content.practicalApplication.content}
                      </MarkdownText>
                    )}

                    {/* Handle structured format with description/steps/tips */}
                    {section.content.practicalApplication.description && (
                      <p className="text-slate-300 leading-relaxed">
                        {section.content.practicalApplication.description}
                      </p>
                    )}

                    {section.content.practicalApplication.steps && (
                      <div>
                        <h5 className="text-sm font-medium text-green-300 uppercase tracking-wider mb-3">Steps</h5>
                        <ul className="space-y-2">
                          {section.content.practicalApplication.steps.map((step, idx) => (
                            <li key={idx} className="flex gap-3 items-start">
                              <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <span className="text-green-300 text-xs font-bold">{idx + 1}</span>
                              </div>
                              <span className="text-slate-300">{step}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {section.content.practicalApplication.tips && (
                      <div className="mt-4 p-4 bg-green-900/20 rounded-lg border border-green-500/20">
                        <h5 className="text-sm font-medium text-green-300 uppercase tracking-wider mb-2 flex items-center gap-2">
                          <Lightbulb className="w-4 h-4" />
                          Tips
                        </h5>
                        <ul className="space-y-2">
                          {section.content.practicalApplication.tips.map((tip, idx) => (
                            <li key={idx} className="flex gap-2 items-start text-slate-300 text-sm">
                              <span className="text-green-400">•</span>
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Common Mistakes */}
        {section.content.commonMistakes && (
          <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-2 border-amber-400/40 rounded-xl p-6 hover:border-amber-400/60 transition-all shadow-lg shadow-amber-500/10">
            <button
              onClick={() => toggleExpanded('commonMistakes')}
              aria-expanded={!!expandedParts.commonMistakes}
              className="w-full text-left group"
            >
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-bold text-amber-200 flex items-center gap-3 group-hover:text-amber-100 transition-colors">
                  <AlertCircle className="w-6 h-6 text-amber-400" />
                  {section.content.commonMistakes.title}
                </h4>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-amber-300 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                    {expandedParts.commonMistakes ? 'Click to collapse' : 'Click to expand'}
                  </span>
                  <ChevronRight
                    className={`w-6 h-6 text-amber-400 transition-transform group-hover:text-amber-300 ${
                      expandedParts.commonMistakes ? 'rotate-90' : ''
                    }`}
                  />
                </div>
              </div>
            </button>
            
            <AnimatePresence>
              {expandedParts.commonMistakes && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="mt-4">
                    <MarkdownText className="text-slate-300 leading-relaxed whitespace-pre-line">
                      {section.content.commonMistakes.content}
                    </MarkdownText>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        <MeetThinkers ids={section.meetThinkers} />
        <SparPrompt title={section.title} module={module} />
      </div>
    </Card>
  );
};

// Exercise Card Component
const ExerciseCard = ({ exercise, index, isCompleted, onComplete, isActive, onClick }) => {
  const typeConfig = {
    observation: {
      color: 'cyan',
      icon: Eye,
      gradient: 'from-cyan-500/20 via-blue-500/20 to-indigo-500/20',
      border: 'border-cyan-500/40',
      iconBg: 'bg-gradient-to-br from-cyan-500/30 to-blue-500/30'
    },
    practice: {
      color: 'green',
      icon: Target,
      gradient: 'from-green-500/20 via-emerald-500/20 to-teal-500/20',
      border: 'border-green-500/40',
      iconBg: 'bg-gradient-to-br from-green-500/30 to-emerald-500/30'
    },
    reflection: {
      color: 'purple',
      icon: Brain,
      gradient: 'from-purple-500/20 via-violet-500/20 to-pink-500/20',
      border: 'border-purple-500/40',
      iconBg: 'bg-gradient-to-br from-purple-500/30 to-violet-500/30'
    },
    'self-assessment': {
      color: 'amber',
      icon: Lightbulb,
      gradient: 'from-amber-500/20 via-orange-500/20 to-yellow-500/20',
      border: 'border-amber-500/40',
      iconBg: 'bg-gradient-to-br from-amber-500/30 to-orange-500/30'
    }
  };

  const config = typeConfig[exercise.type] || typeConfig.practice;
  const ExerciseIcon = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <Card
        hover={true}
        className={`p-0 cursor-pointer transition-all overflow-hidden ${
          isActive ? 'ring-2 ring-conversation-teal shadow-xl shadow-conversation-teal/20' : ''
        } ${isCompleted ? 'border-green-500/50' : ''}`}
        onClick={onClick}
        role="button"
        tabIndex={0}
        aria-expanded={isActive}
        aria-label={`${exercise.title}${isCompleted ? ', completed' : ''}`}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      >
        {/* Header with gradient background */}
        <div className={`bg-gradient-to-r ${config.gradient} p-6 relative`}>
          <div className="flex items-start gap-4">
            <motion.div
              className={`w-16 h-16 ${config.iconBg} rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg`}
              whileHover={{ scale: 1.05, rotate: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <ExerciseIcon className={`w-8 h-8 text-${config.color}-300`} />
            </motion.div>

            <div className="flex-1">
              <div className="flex items-start justify-between gap-3 mb-3">
                <h4 className="text-xl font-bold text-white leading-tight">{exercise.title}</h4>
                {isCompleted && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring" }}
                  >
                    <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
                  </motion.div>
                )}
              </div>

              <div className="flex items-center flex-wrap gap-2 mb-3">
                {exercise.type && (
                  <span className={`px-3 py-1 bg-${config.color}-500/30 text-${config.color}-200 rounded-full text-xs font-medium border border-${config.color}-400/30`}>
                    {exercise.type.replace('_', ' ').replace('-', ' ')}
                  </span>
                )}
                {exercise.duration && (
                  <span className="flex items-center gap-1 px-3 py-1 bg-white/10 rounded-full text-xs text-slate-300">
                    <Clock className="w-3 h-3" />
                    {exercise.duration}
                  </span>
                )}
                {!isActive && (
                  <span className="flex items-center gap-1 px-3 py-1 bg-white/10 rounded-full text-xs text-slate-300">
                    <ChevronRight className="w-3 h-3" />
                    Click to expand
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="p-6 pb-4">
          <MarkdownText className="text-slate-300 leading-relaxed">
            {exercise.description}
          </MarkdownText>
        </div>

          {isActive && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="px-6 pb-6"
            >
              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent mb-6" />

              {/* Instructions with visual steps */}
              <div className="mb-6">
                <h5 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-conversation-teal" />
                  Step-by-Step Instructions
                </h5>
                <div className="space-y-3">
                  {(exercise.steps || exercise.instructions || []).map((instruction, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="flex gap-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700/50 hover:border-conversation-teal/50 transition-all group"
                    >
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-conversation-teal to-bright-gold flex items-center justify-center text-white font-bold text-sm shadow-lg">
                          {idx + 1}
                        </div>
                      </div>
                      <div className="flex-1 pt-1">
                        <MarkdownText className="text-slate-200 group-hover:text-white transition-colors">
                          {instruction}
                        </MarkdownText>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Journal hand-off: every "write it down" has somewhere to land */}
              <Link
                to="/journal"
                state={{ prompt: exercise.title }}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-lg border border-bright-gold/40 text-bright-gold hover:bg-bright-gold/10 transition-colors text-sm font-medium"
              >
                <Feather className="w-4 h-4" />
                Write it in your journal
              </Link>

              {/* Science Connection */}
              {exercise.scienceConnection && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-5 bg-gradient-to-br from-blue-900/30 to-indigo-900/30 rounded-xl border-2 border-blue-500/30 hover:border-blue-400/50 transition-all"
                >
                  <h6 className="text-blue-300 font-bold text-base mb-3 flex items-center gap-2">
                    <Beaker className="w-5 h-5" />
                    Science Connection
                  </h6>
                  <MarkdownText className="text-slate-200 leading-relaxed">
                    {exercise.scienceConnection}
                  </MarkdownText>
                </motion.div>
              )}

              {/* Real World Variation */}
              {exercise.realWorldVariation && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="mb-6 p-5 bg-gradient-to-br from-green-900/30 to-emerald-900/30 rounded-xl border-2 border-green-500/30 hover:border-green-400/50 transition-all"
                >
                  <h6 className="text-green-300 font-bold text-base mb-3 flex items-center gap-2">
                    <Coffee className="w-5 h-5" />
                    Real World Variation
                  </h6>
                  <MarkdownText className="text-slate-200 leading-relaxed">
                    {exercise.realWorldVariation}
                  </MarkdownText>
                </motion.div>
              )}

              {/* Reflection Questions */}
              {exercise.reflection && exercise.reflection.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mb-6 p-5 bg-gradient-to-br from-purple-900/30 to-violet-900/30 rounded-xl border-2 border-purple-500/30 hover:border-purple-400/50 transition-all"
                >
                  <h6 className="text-purple-300 font-bold text-base mb-3 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5" />
                    Reflection Questions
                  </h6>
                  <ul className="space-y-3">
                    {exercise.reflection.map((question, idx) => (
                      <li key={idx} className="flex gap-3 items-start">
                        <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-purple-400 text-xs font-bold">{idx + 1}</span>
                        </div>
                        <MarkdownText className="text-slate-200 flex-1">
                          {question}
                        </MarkdownText>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}

              {/* Mark Complete Button */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                {!isCompleted ? (
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      onComplete();
                    }}
                    className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold px-6 py-2 shadow-lg hover:shadow-green-500/50 transition-all"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark as Complete
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Completed</span>
                  </div>
                )}
              </div>
            </motion.div>
          )}
      </Card>
    </motion.div>
  );
};

// Main Module Detail Component
const PhilosophyModuleDetail = ({ module, onBack, onComplete, initialSection = null, navKey = null }) => {
  const { 
    getModuleProgress, 
    updateModuleProgress,
    completeModule,
    isModuleCompleted
  } = useApp();
  
  // Remember where the reader was, so a tool opened from anywhere (the
  // Toolkit nav, a deep link) can offer the way back — not just tools
  // reached through this module's own "Practise This Now" cards.
  useEffect(() => {
    try {
      localStorage.setItem('profoundPhilosophy_lastModule', JSON.stringify({
        id: module.id, title: module.title, at: Date.now()
      }));
    } catch { /* private mode — the link simply will not appear */ }
  }, [module.id, module.title]);

  const savedProgress = getModuleProgress(module.id) || {};
  const moduleIsCompleted = isModuleCompleted(module.id);
  
  // Section pages sit at index section+1 (page 0 is the overview). A profile's
  // "argument you met" link names a section; clamp in case content shrinks.
  const sectionToPage = (idx) => Math.min(idx + 1, module.content.sections.length);
  const [currentPage, setCurrentPage] = useState(
    initialSection != null ? sectionToPage(initialSection) : (savedProgress.currentPage || 0)
  );
  const [completedSections, setCompletedSections] = useState(savedProgress.completedSections || []);
  const [completedExercises, setCompletedExercises] = useState(savedProgress.completedExercises || []);
  const [activeExercise, setActiveExercise] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationMessage, setCelebrationMessage] = useState("Section Complete");
  const [showCertificate, setShowCertificate] = useState(false);
  const [showCourseMastery, setShowCourseMastery] = useState(false);
  
  // Honour a section deep link on every navigation, not only first mount —
  // the reader may follow a second profile link while this page stays mounted.
  useEffect(() => {
    if (initialSection != null) setCurrentPage(sectionToPage(initialSection));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navKey, module.id]);

  // Scroll to top when page changes
  useEffect(() => {
    // 'instant', not 'smooth': the AnimatePresence page swap cancels a smooth
    // scroll mid-flight, stranding the reader partway down the new page.
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [currentPage]);
  
  // Every module file authors its exercises inside `content`, not at the top level.
  const moduleExercises = module.content?.exercises || [];

  // Create pages from module content
  const pages = [
    { type: 'overview', title: 'Overview', icon: BookOpen },
    ...module.content.sections.map((section, idx) => ({
      type: 'section',
      index: idx,
      title: section.title,
      icon: Brain
    })),
    // Module-level callouts get their own reading pages (they were never rendered before)
    ...(module.content.callouts?.practicalApplication ? [{ type: 'callout-practical', title: 'Putting It Into Practice', icon: Target }] : []),
    ...(module.content.callouts?.deepDive ? [{ type: 'callout-deepdive', title: 'Going Deeper', icon: Brain }] : []),
    ...(module.content.callouts?.commonMistakes ? [{ type: 'callout-mistakes', title: 'Common Mistakes', icon: AlertCircle }] : []),
    // Only include the exercises page if the module actually has exercises
    ...(moduleExercises.length > 0 ? [{ type: 'exercises', title: 'Practice Exercises', icon: Target }] : []),
    // Only include FAQ page if module has FAQs
    ...(module.faqs && module.faqs.length > 0 ? [{ type: 'faqs', title: 'FAQs', icon: MessageSquare }] : []),
    // Only include Resources page if module has resources
    ...(module.resources && module.resources.length > 0 ? [{ type: 'resources', title: 'Resources', icon: BookOpen }] : []),
    { type: 'takeaways', title: 'Key Takeaways', icon: Award },
    // The Next Steps page only exists when a module actually authors next steps
    // (none currently do; an unconditional page here used to end every module on a blank)
    ...((module.nextSteps || module.content?.nextSteps) ? [{ type: 'nextsteps', title: 'Next Steps', icon: ArrowRight }] : [])
  ];

  const currentPageData = pages[currentPage];
  
  // Calculate progress
  const totalItems =
    module.content.sections.length +
    (moduleExercises?.length || 0);

  const completedItems =
    completedSections.length +
    completedExercises.length;
  
  const progressPercentage = Math.round((completedItems / totalItems) * 100);

  // Save progress
  useEffect(() => {
    const saveProgress = async () => {
      await updateModuleProgress(module.id, {
        currentPage,
        completedSections,
        completedExercises,
        progressPercentage,
        lastUpdated: new Date().toISOString()
      });
    };

    const timeoutId = setTimeout(saveProgress, 500);
    return () => clearTimeout(timeoutId);
  }, [currentPage, completedSections, completedExercises, module.id]);

  const handleSectionComplete = (sectionIndex) => {
    if (!completedSections.includes(sectionIndex)) {
      setCompletedSections([...completedSections, sectionIndex]);
    }
  };

  const handleExerciseComplete = (exerciseIndex) => {
    if (!completedExercises.includes(exerciseIndex)) {
      setCompletedExercises([...completedExercises, exerciseIndex]);
    }
  };

  const handleModuleComplete = async () => {
    await completeModule(module.id, {
      completedSections,
      completedExercises,
      progressPercentage: 100,
      completedAt: new Date().toISOString()
    });

    // Award gamification points for module completion
    const gamification = useGamificationStore.getState();
    gamification.completeModule(module.id);

    // Check if all modules are now complete (get fresh state after completeModule)
    const updatedGamification = useGamificationStore.getState();
    const allModules = getAllModules();
    const completedModulesList = updatedGamification.completedModules;

    const allComplete = allModules.every(m => completedModulesList.includes(m.id));

    // Show special course mastery certificate if all 8 modules complete
    if (allComplete && allModules.length === 8) {
      // Award bonus points for course completion
      updatedGamification.addPoints(500, 'Course Mastery Achieved 🏆');
      setShowCourseMastery(true);
    } else {
      // Show regular module certificate
      setShowCertificate(true);
    }

    // Call onComplete callback after a delay (after user closes certificate)
    // onComplete?.();
  };

  const handleContinue = () => {
    const pageType = currentPageData.type;

    // Auto-complete current page
    if (pageType === 'section' && !completedSections.includes(currentPageData.index)) {
      handleSectionComplete(currentPageData.index);
    } else if (pageType === 'exercises' && moduleExercises && moduleExercises.length > 0) {
      // Mark all exercises as complete
      const allExerciseIndices = moduleExercises.map((_, idx) => idx);
      setCompletedExercises(allExerciseIndices);
    }

    // If on last page, complete the module
    if (currentPage === pages.length - 1) {
      // Show celebration first
      setCelebrationMessage("Module Complete 🎉");
      setShowCelebration(true);

      // Complete module after celebration
      setTimeout(() => {
        handleModuleComplete();
      }, 2000);
    } else {
      // Show celebration for regular page completion
      setCelebrationMessage("Well Done 🌟");
      setShowCelebration(true);

      // Move to next page after celebration
      setTimeout(() => {
        setCurrentPage(currentPage + 1);
      }, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal to-gunmetal pt-24 md:pt-32">
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-20 sm:pb-12 max-w-6xl">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-pearl/60 hover:text-pearl mb-8 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Modules
        </button>

        {/* Full module header on the intro page only; once reading, a compact
            strip keeps progress visible without pushing content below the fold */}
        {currentPage === 0 ? (
        <div className="relative mb-8 rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-gunmetal to-charcoal">
          {/* Content */}
          <div className="relative p-8">
            <div className="flex flex-col items-center text-center mb-6">
              {/* Centered Full Image Icon */}
              {module.heroImage ? (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="mb-6"
                >
                  <img
                    src={module.heroImage}
                    alt={module.title}
                    className="w-44 h-44 object-contain rounded-2xl"
                  />
                </motion.div>
              ) : (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  whileHover={{ scale: 1.05, rotate: 5 }}
                  className="w-32 h-32 bg-gradient-to-br from-conversation-teal via-bright-gold to-purple-500 rounded-2xl flex items-center justify-center shadow-lg mb-6"
                >
                  <BookOpen className="w-16 h-16 text-white" />
                </motion.div>
              )}

              <div className="mb-6">
                <h1 className="text-2xl md:text-4xl font-bold text-white mb-2">
                  {module.title}
                </h1>
                <p className="text-xl text-cyan-300">{module.subtitle}</p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-6">
              <div className="flex-1 w-full">

                <MarkdownText className="text-pearl/90 mb-6 text-base md:text-lg leading-relaxed text-center">
                  {module.description}
                </MarkdownText>

                <div className="flex items-center justify-center flex-wrap gap-2 md:gap-4 text-xs md:text-sm">
                  <span className="flex items-center gap-1 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                    <Clock className="w-3 h-3 md:w-4 md:h-4 text-conversation-teal" />
                    <span className="text-pearl font-medium">{module.duration}</span>
                  </span>
                  {module.difficulty && (
                    <span className={`px-3 md:px-4 py-1.5 md:py-2 rounded-full font-medium border backdrop-blur-sm ${
                      module.difficulty === 'Foundation'
                        ? 'bg-green-500/20 text-green-300 border-green-500/30'
                        : module.difficulty === 'Essential'
                        ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
                        : 'bg-red-500/20 text-red-300 border-red-500/30'
                    }`}>
                      {module.difficulty}
                    </span>
                  )}
                  {module.prerequisites && module.prerequisites.length > 0 && (
                    <span className="px-3 md:px-4 py-1.5 md:py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 text-pearl">
                      Prerequisites: {module.prerequisites.length}
                    </span>
                  )}
                </div>
              </div>

              {/* Progress Circle */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex-shrink-0"
              >
                <div className="relative w-24 h-24 md:w-32 md:h-32 bg-white/10 backdrop-blur-sm rounded-full p-2 border-2 border-white/20">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 112 112">
                    <circle
                      cx="56" cy="56" r="50"
                      fill="none"
                      stroke="rgba(255,255,255,0.1)"
                      strokeWidth="8"
                    />
                    <motion.circle
                      cx="56" cy="56" r="50"
                      fill="none"
                      stroke="url(#progressGradient)"
                      strokeWidth="8"
                      strokeDasharray={`${2 * Math.PI * 50}`}
                      strokeDashoffset={`${2 * Math.PI * 50 * (1 - progressPercentage/100)}`}
                      strokeLinecap="round"
                      initial={{ strokeDashoffset: `${2 * Math.PI * 50}` }}
                      animate={{ strokeDashoffset: `${2 * Math.PI * 50 * (1 - progressPercentage/100)}` }}
                      transition={{ duration: 1, ease: "easeOut" }}
                    />
                    <defs>
                      <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#14b8a6" />
                        <stop offset="100%" stopColor="#d4af37" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl md:text-3xl font-bold text-white">{progressPercentage}%</span>
                    {moduleIsCompleted && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring" }}
                      >
                        <CheckCircle className="w-6 h-6 md:w-8 md:h-8 text-green-400 mt-1" />
                      </motion.div>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
        ) : (
        <div className="relative flex items-center justify-between gap-4 mb-6 px-4 py-3 rounded-xl overflow-hidden border border-white/10">
          {/* The module's own sky, as a thin band behind the strip */}
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <img
              src={`/images/cloud-${module.order?.toString().padStart(2, '0')}.webp`}
              alt=""
              className="w-full h-full object-cover opacity-60"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-gunmetal/85 via-gunmetal/60 to-gunmetal/85" />
          </div>
          <div className="relative min-w-0">
            <h2 className="text-white font-bold text-base leading-snug">{module.title}</h2>
            <p className="text-xs text-slate-300" aria-live="polite">Page {currentPage + 1} of {pages.length}</p>
          </div>
          <div className="relative flex items-center gap-3 flex-shrink-0">
            <div className="w-28 sm:w-40 bg-slate-900/60 rounded-full h-2">
              <div
                className="h-2 rounded-full bg-gradient-to-r from-conversation-teal to-bright-gold transition-all"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <span className="text-sm font-bold text-white">{progressPercentage}%</span>
          </div>
        </div>
        )}

        {/* Progress Dots Only */}
        {/* p-1.5 + bg-clip-content: the visible dot stays 12px while the button
            itself is 24px, WCAG 2.2 target size, and easier thumbing. */}
        <nav aria-label="Module pages" className="flex flex-wrap items-center justify-center gap-0 mb-8">
          {pages.map((page, idx) => (
            <motion.button
              key={idx}
              onClick={() => setCurrentPage(idx)}
              whileHover={{ scale: 1.2 }}
              className={`rounded-full p-1.5 bg-clip-content transition-all ${
                idx === currentPage
                  ? 'w-[3.25rem] h-6 bg-gradient-to-r from-conversation-teal to-bright-gold'
                  : idx < currentPage
                  ? 'w-6 h-6 bg-green-500'
                  : 'w-6 h-6 bg-slate-500 hover:bg-slate-400'
              }`}
              title={page.title}
              aria-label={`Page ${idx + 1} of ${pages.length}: ${page.title}`}
              aria-current={idx === currentPage ? 'page' : undefined}
            />
          ))}
        </nav>

        {/* Page Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="mb-6"
          >
            {/* Overview Page */}
            {currentPageData.type === 'overview' && (
              <Card hover={false} blur={false} className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Module Overview</h2>

                <div className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 border-l-4 border-indigo-500 rounded-r-xl p-6 mb-8">
                  <MarkdownText className="text-slate-200 text-lg leading-relaxed first-letter:text-5xl first-letter:font-bold first-letter:text-indigo-400 first-letter:mr-3 first-letter:float-left">
                    {module.content.overview}
                  </MarkdownText>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-green-400" />
                    Learning Objectives
                  </h3>
                  <ul className="space-y-3">
                    {module.content.learningObjectives.map((objective, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-conversation-teal text-sm">{idx + 1}</span>
                        </div>
                        <MarkdownText className="text-pearl/80">
                          {objective}
                        </MarkdownText>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            )}

            {/* Section Pages */}
            {currentPageData.type === 'section' && (
              <>
                <SectionContent
                  section={module.content.sections[currentPageData.index]}
                  module={module}
                  sectionIndex={currentPageData.index}
                  isCompleted={completedSections.includes(currentPageData.index)}
                  onComplete={() => handleSectionComplete(currentPageData.index)}
                />
                {/* Canonical forks: commit to a position while the argument is fresh */}
                {getPointsForPlacement(module.order, currentPageData.index).map(point => (
                  <PositionPrompt key={point.id} point={point} />
                ))}
              </>
            )}

            {/* Exercises Page */}
            {currentPageData.type === 'exercises' && (
              <div className="space-y-6">
                {/* Enhanced header */}
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card hover={false} blur={false} className="p-8 bg-gradient-to-br from-slate-800/50 to-slate-900/50 border-2 border-conversation-teal/30">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-conversation-teal to-bright-gold rounded-2xl flex items-center justify-center shadow-xl">
                        <Target className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-3xl font-bold text-white mb-2">Practice Exercises</h2>
                        <p className="text-slate-300 text-lg">
                          Apply what you've learned with hands-on practice
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/20">
                      <Lightbulb className="w-6 h-6 text-bright-gold flex-shrink-0" />
                      <p className="text-slate-200 text-sm">
                        <strong className="text-white">Tip:</strong> Click any exercise card to expand full instructions, science connections, and reflection questions.
                        Each exercise type has its own colour coding to help you navigate.
                      </p>
                    </div>

                    {/* Enhanced Progress indicator */}
                    <div className="mt-6 pt-6 border-t border-slate-700">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-slate-300">Exercise Progress</span>
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-white">
                            {completedExercises.length}
                          </span>
                          <span className="text-sm text-slate-400">of</span>
                          <span className="text-lg font-bold text-slate-300">
                            {(moduleExercises || []).length}
                          </span>
                          <span className="text-sm text-slate-400">completed</span>
                        </div>
                      </div>

                      {/* Progress bar with glow */}
                      <div className="relative">
                        <div className="w-full h-4 bg-slate-800 rounded-full overflow-hidden border border-slate-700 shadow-inner">
                          <motion.div
                            className="h-full bg-gradient-to-r from-conversation-teal via-cyan-400 to-bright-gold relative shadow-lg"
                            initial={{ width: 0 }}
                            animate={{
                              width: `${((completedExercises.length / (moduleExercises || []).length) * 100)}%`
                            }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            style={{
                              boxShadow: '0 0 20px rgba(20, 184, 166, 0.6), 0 0 40px rgba(212, 175, 55, 0.4)'
                            }}
                          >
                            {/* Animated shine effect */}
                            <motion.div
                              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                              animate={{
                                x: ['-100%', '200%']
                              }}
                              transition={{
                                duration: 2,
                                repeat: Infinity,
                                repeatDelay: 1,
                                ease: "easeInOut"
                              }}
                            />
                          </motion.div>
                        </div>

                        {/* Percentage badge */}
                        <motion.div
                          className="absolute -top-1 bg-gradient-to-r from-conversation-teal to-bright-gold text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg border-2 border-slate-900"
                          initial={{ left: 0 }}
                          animate={{
                            left: `calc(${((completedExercises.length / (moduleExercises || []).length) * 100)}% - 28px)`
                          }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                          {Math.round((completedExercises.length / (moduleExercises || []).length) * 100)}%
                        </motion.div>
                      </div>

                      {/* Completion message */}
                      {completedExercises.length === (moduleExercises || []).length && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-3 flex items-center justify-center gap-2 text-green-400"
                        >
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-medium">All exercises completed. Great work 🎉</span>
                        </motion.div>
                      )}
                    </div>
                  </Card>
                </motion.div>

                {/* Exercise cards */}
                {(moduleExercises || []).map((exercise, idx) => (
                  <ExerciseCard
                    key={idx}
                    exercise={exercise}
                    index={idx}
                    isCompleted={completedExercises.includes(idx)}
                    onComplete={() => handleExerciseComplete(idx)}
                    isActive={activeExercise === idx}
                    onClick={() => setActiveExercise(activeExercise === idx ? null : idx)}
                  />
                ))}
              </div>
            )}

            {/* FAQs Page */}
            {currentPageData.type === 'faqs' && (
              <div className="space-y-6">
                <Card hover={false} blur={false} className="p-6">
                  <h2 className="text-2xl font-bold text-white mb-4">Frequently Asked Questions</h2>
                  <p className="text-slate-300 mb-6">
                    Common questions about this module's content, answered.
                  </p>
                </Card>

                <div className="space-y-4">
                  {(module.faqs || []).map((faq, idx) => (
                    <Card key={idx} blur={false} className="p-6">
                      <h3 className="text-lg font-semibold text-white mb-3 flex items-start gap-3">
                        <MessageSquare className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" />
                        {faq.question}
                      </h3>
                      <MarkdownText className="text-slate-300 leading-relaxed">
                        {faq.answer}
                      </MarkdownText>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Resources Page */}
            {currentPageData.type === 'resources' && (
              <div className="space-y-6">
                <Card hover={false} blur={false} className="p-6">
                  <h2 className="text-2xl font-bold text-white mb-4">Recommended Resources</h2>
                  <p className="text-slate-300 mb-6">
                    Dive deeper with these curated resources specific to this module's content.
                  </p>
                </Card>

                <div className="space-y-4">
                  {(module.resources || []).map((resource, idx) => (
                    <Card key={idx} className="p-6 hover:border-conversation-teal/50 transition-all" hover={true} blur={false}>
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                          <BookOpen className="w-6 h-6 text-purple-400" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1">
                            {resource.title}
                          </h3>
                          {resource.author && (
                            <p className="text-purple-300 text-sm font-medium mb-2">
                              by {resource.author}
                            </p>
                          )}
                          <p className="text-slate-300 text-sm mb-2">
                            {resource.description}
                          </p>
                          {resource.why && (
                            <p className="text-slate-400 text-sm italic mb-3">
                              Why read this: {resource.why}
                            </p>
                          )}
                          <div className="flex items-center flex-wrap gap-3">
                            <span className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded">
                              {resource.type}
                            </span>
                            {resource.url && (
                              <a
                                href={resource.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-conversation-teal hover:text-bright-gold text-sm flex items-center gap-1 transition-colors"
                              >
                                Visit Resource
                                <ArrowRight className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Takeaways Page */}
            {/* Callout Pages: the module-level callouts (previously invisible) */}
            {(currentPageData.type === 'callout-practical' ||
              currentPageData.type === 'callout-deepdive' ||
              currentPageData.type === 'callout-mistakes') && (() => {
              const calloutMap = {
                'callout-practical': {
                  data: module.content.callouts?.practicalApplication,
                  icon: Target,
                  accent: 'border-emerald-500',
                  wash: 'from-emerald-500/10 via-teal-500/10 to-cyan-500/10'
                },
                'callout-deepdive': {
                  data: module.content.callouts?.deepDive,
                  icon: Brain,
                  accent: 'border-indigo-500',
                  wash: 'from-indigo-500/10 via-purple-500/10 to-pink-500/10'
                },
                'callout-mistakes': {
                  data: module.content.callouts?.commonMistakes,
                  icon: AlertCircle,
                  accent: 'border-amber-500',
                  wash: 'from-amber-500/10 via-orange-500/10 to-rose-500/10'
                }
              };
              const { data, icon: CalloutIcon, accent, wash } = calloutMap[currentPageData.type];
              if (!data) return null;
              return (
                <Card hover={false} blur={false} className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <CalloutIcon className="w-7 h-7 text-slate-200" />
                    <h2 className="text-2xl font-bold text-white">{data.title}</h2>
                  </div>
                  <div className={`bg-gradient-to-r ${wash} border-l-4 ${accent} rounded-r-xl p-6`}>
                    <MarkdownText className="text-slate-300 text-base leading-relaxed whitespace-pre-line">
                      {data.content}
                    </MarkdownText>
                  </div>
                </Card>
              );
            })()}

            {currentPageData.type === 'takeaways' && (
              <>
              <Card hover={false} blur={false} className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Key Takeaways</h2>

                {(module.keyTakeaways || module.content?.keyTakeaways) && (
                  <div className="space-y-4 mb-8">
                    {(module.keyTakeaways || module.content.keyTakeaways).map((takeaway, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          {typeof takeaway === 'string' ? (
                            <MarkdownText className="text-slate-300">
                              {takeaway}
                            </MarkdownText>
                          ) : (
                            <>
                              {takeaway.title && (
                                <div className="font-semibold text-slate-200 mb-1">{takeaway.title}</div>
                              )}
                              <MarkdownText className="text-slate-300">
                                {takeaway.content}
                              </MarkdownText>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reflection Questions */}
                {(module.reflectionQuestions || module.content?.reflectionQuestions) && (
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                      <Lightbulb className="w-7 h-7 text-bright-gold" />
                      Reflection Questions
                    </h3>
                    <p className="text-slate-300 mb-6">
                      Take a moment to reflect on what you've learned and how it applies to your own life and interactions.
                    </p>
                    <div className="space-y-4">
                      {(module.reflectionQuestions || module.content.reflectionQuestions).map((question, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className="flex gap-4 p-5 bg-gradient-to-r from-purple-500/10 to-violet-500/10 rounded-lg border border-purple-500/30 hover:border-purple-400/50 transition-all"
                        >
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center text-white font-bold shadow-lg">
                              {idx + 1}
                            </div>
                          </div>
                          <div className="flex-1 pt-1">
                            <MarkdownText className="text-slate-200 leading-relaxed">
                              {question}
                            </MarkdownText>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Practise this now: the toolkit tools paired with this module.
                    Learning lands when it's used the same day; these deep-link
                    straight into the matching tool. */}
                {getToolsForModule(module.order).length > 0 && (
                  <div className="mt-8 pt-8 border-t border-slate-700">
                    <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
                      <Target className="w-7 h-7 text-conversation-teal" />
                      Practise This Now
                    </h3>
                    <p className="text-slate-300 mb-5">
                      The ideas you just read have matching tools in the Toolkit.
                      The best time to use one is today, while the module is fresh.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {getToolsForModule(module.order).map((tool) => (
                        <Link
                          key={tool.id}
                          to={`/toolkit/${tool.id}`}
                          state={{ fromModuleId: module.id, fromModuleTitle: module.title }}
                          className="group flex items-center justify-between gap-3 p-4 rounded-xl border border-conversation-teal/30 bg-conversation-teal/5 hover:bg-conversation-teal/15 hover:border-conversation-teal/60 transition-all"
                        >
                          <div>
                            <div className="font-semibold text-slate-100 group-hover:text-white">{tool.title}</div>
                            <div className="text-sm text-slate-400">{tool.time}</div>
                          </div>
                          <ArrowRight className="w-5 h-5 text-conversation-teal flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* End-of-course signpost, authored by the module that ends
                    the arc (courseFinale in its content). The reader finishes
                    the last module exactly when curiosity about the PEOPLE
                    peaks, so the send-off names the pulls specifically and
                    points at The Lineage and the Resource Library. */}
                {module.content?.courseFinale && (
                  <div className="mt-8 pt-8 border-t border-slate-700">
                    <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
                      <Map className="w-7 h-7 text-bright-gold" />
                      {module.content.courseFinale.title}
                    </h3>
                    <MarkdownText className="text-slate-300 mb-5 leading-relaxed">
                      {module.content.courseFinale.intro}
                    </MarkdownText>
                    <div className="space-y-3">
                      {(module.content.courseFinale.pulls || []).map((pull, idx) => (
                        <Link
                          key={idx}
                          to={pull.to}
                          className="group block p-4 rounded-xl border border-bright-gold/20 bg-bright-gold/5 hover:bg-bright-gold/10 hover:border-bright-gold/50 transition-all"
                        >
                          <div className="font-semibold text-slate-100 group-hover:text-white mb-1">{pull.hook}</div>
                          <div className="text-sm text-slate-400 flex items-center justify-between gap-3">
                            <span className="min-w-0">{pull.text}</span>
                            <ArrowRight className="w-4 h-4 text-bright-gold flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </Link>
                      ))}
                    </div>
                    {module.content.courseFinale.message && (
                      <MarkdownText className="text-slate-400 text-sm mt-5 leading-relaxed">
                        {module.content.courseFinale.message}
                      </MarkdownText>
                    )}
                  </div>
                )}

                {/* Re-entry note: the walk back to the ordinary world. Deep
                    reading borrows the attention the mundane runs on and returns
                    it a few minutes late; naming that here is kinder than letting
                    readers discover it at the kettle. */}
                <div className="mt-8 p-5 rounded-xl bg-indigo-500/10 border border-indigo-400/30">
                  <div className="flex items-start gap-3">
                    <Coffee className="w-5 h-5 text-indigo-300 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-indigo-200 font-semibold mb-1">Before you go back</h4>
                      <p className="text-slate-300 text-sm leading-relaxed">
                        You've been somewhere abstract, and the walk back takes a minute.
                        If the room feels slightly less solid than you left it, or you reach
                        for a spoon and find it was the cinnamon you wanted, nothing is wrong.
                        Deep thought borrows the attention the mundane usually runs on, and
                        returns it a few minutes late. It passes. Make the next cup of coffee
                        slowly, and consider it part of the module.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Complete Module Button: lives here because Takeaways is the final page */}
                {progressPercentage >= 80 && (
                  <div className="mt-8 pt-8 border-t border-slate-700">
                    <div className="text-center">
                      <p className="text-slate-300 mb-4 text-lg">
                        Ready to celebrate your achievement?
                      </p>
                      <Button
                        onClick={handleModuleComplete}
                        size="lg"
                        className="bg-gradient-to-r from-conversation-teal to-bright-gold hover:shadow-2xl hover:shadow-conversation-teal/50 transition-all"
                      >
                        <Award className="w-5 h-5 mr-2" />
                        Complete Module & Get Certificate
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
              {/* The parting gesture: one question to carry out the door */}
              <TableCard module={module} />
              </>
            )}

            {/* Next Steps Page */}
            {currentPageData.type === 'nextsteps' && (
              <Card hover={false} blur={false} className="p-8">
                <div className="mb-8">
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-6"
                  >
                    <motion.div
                      className="w-24 h-24 bg-gradient-to-br from-conversation-teal via-cyan-400 to-bright-gold rounded-3xl flex items-center justify-center shadow-2xl mx-auto mb-6"
                      whileHover={{ scale: 1.05, rotate: 5 }}
                      transition={{ type: "spring", stiffness: 300 }}
                      style={{ boxShadow: '0 10px 40px rgba(20, 184, 166, 0.4), 0 0 60px rgba(212, 175, 55, 0.3)' }}
                    >
                      <ArrowRight className="w-12 h-12 text-white" />
                    </motion.div>
                    <h2 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-conversation-teal via-cyan-300 to-bright-gold mb-4">
                      Next Steps
                    </h2>
                    <p className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-rose-400">
                      Your Journey Continues Here
                    </p>
                  </motion.div>
                </div>

                {(module.nextSteps || module.content?.nextSteps) && (
                  <div>
                    {(() => {
                      const nextSteps = module.nextSteps || module.content.nextSteps;

                      // Handle object structure with immediate/thisWeek/longTerm (Module 03+ format)
                      if (nextSteps && typeof nextSteps === 'object' && !Array.isArray(nextSteps) && (nextSteps.immediate || nextSteps.thisWeek || nextSteps.longTerm)) {
                        return (
                          <div className="space-y-8">
                            {/* Immediate Steps */}
                            {nextSteps.immediate && nextSteps.immediate.length > 0 && (
                              <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                              >
                                <div className="flex items-center gap-3 mb-4">
                                  <motion.div
                                    className="w-12 h-12 bg-gradient-to-br from-green-400 via-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-xl"
                                    whileHover={{ scale: 1.1, rotate: 10 }}
                                    style={{ boxShadow: '0 8px 30px rgba(34, 197, 94, 0.5)' }}
                                  >
                                    <Zap className="w-6 h-6 text-white" />
                                  </motion.div>
                                  <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">
                                    Immediate Actions (This Week)
                                  </h3>
                                </div>
                                <div className="space-y-3">
                                  {nextSteps.immediate.map((step, idx) => (
                                    <motion.div
                                      key={idx}
                                      initial={{ opacity: 0, x: -20 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ delay: 0.2 + idx * 0.08 }}
                                      whileHover={{ scale: 1.02, x: 5 }}
                                      className="group relative flex items-start gap-4 p-5 bg-gradient-to-r from-green-500/20 via-emerald-500/15 to-green-500/20 rounded-xl border-2 border-green-500/40 hover:border-green-400/70 transition-all cursor-default overflow-hidden"
                                      style={{ boxShadow: '0 4px 20px rgba(34, 197, 94, 0.15)' }}
                                    >
                                      {/* Animated background shimmer */}
                                      <motion.div
                                        className="absolute inset-0 bg-gradient-to-r from-transparent via-green-400/10 to-transparent"
                                        animate={{ x: ['-100%', '200%'] }}
                                        transition={{ duration: 3, repeat: Infinity, repeatDelay: 2, ease: "easeInOut" }}
                                      />
                                      <div className="relative z-10 w-6 h-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-lg group-hover:shadow-green-400/50 transition-shadow">
                                        <CheckCircle className="w-4 h-4 text-white" />
                                      </div>
                                      <MarkdownText className="relative z-10 text-slate-100 group-hover:text-white transition-colors leading-relaxed">
                                        {step}
                                      </MarkdownText>
                                    </motion.div>
                                  ))}
                                </div>
                              </motion.div>
                            )}

                            {/* This Week Steps */}
                            {nextSteps.thisWeek && nextSteps.thisWeek.length > 0 && (
                              <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                              >
                                <div className="flex items-center gap-3 mb-4">
                                  <motion.div
                                    className="w-12 h-12 bg-gradient-to-br from-cyan-400 via-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-xl"
                                    whileHover={{ scale: 1.1, rotate: -10 }}
                                    style={{ boxShadow: '0 8px 30px rgba(6, 182, 212, 0.5)' }}
                                  >
                                    <Target className="w-6 h-6 text-white" />
                                  </motion.div>
                                  <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                                    Practice This Week
                                  </h3>
                                </div>
                                <div className="space-y-3">
                                  {nextSteps.thisWeek.map((step, idx) => (
                                    <motion.div
                                      key={idx}
                                      initial={{ opacity: 0, x: -20 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ delay: 0.3 + idx * 0.08 }}
                                      whileHover={{ scale: 1.02, x: 5 }}
                                      className="group relative flex items-start gap-4 p-5 bg-gradient-to-r from-cyan-500/20 via-blue-500/15 to-indigo-500/20 rounded-xl border-2 border-cyan-500/40 hover:border-cyan-400/70 transition-all cursor-default overflow-hidden"
                                      style={{ boxShadow: '0 4px 20px rgba(6, 182, 212, 0.15)' }}
                                    >
                                      {/* Animated background shimmer */}
                                      <motion.div
                                        className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/10 to-transparent"
                                        animate={{ x: ['-100%', '200%'] }}
                                        transition={{ duration: 3, repeat: Infinity, repeatDelay: 2, ease: "easeInOut" }}
                                      />
                                      <div className="relative z-10 w-6 h-6 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-lg group-hover:shadow-cyan-400/50 transition-shadow">
                                        <CheckCircle className="w-4 h-4 text-white" />
                                      </div>
                                      <MarkdownText className="relative z-10 text-slate-100 group-hover:text-white transition-colors leading-relaxed">
                                        {step}
                                      </MarkdownText>
                                    </motion.div>
                                  ))}
                                </div>
                              </motion.div>
                            )}

                            {/* Long-term Steps */}
                            {nextSteps.longTerm && nextSteps.longTerm.length > 0 && (
                              <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                              >
                                <div className="flex items-center gap-3 mb-4">
                                  <motion.div
                                    className="w-12 h-12 bg-gradient-to-br from-purple-400 via-violet-500 to-fuchsia-600 rounded-xl flex items-center justify-center shadow-xl"
                                    whileHover={{ scale: 1.1, rotate: 10 }}
                                    style={{ boxShadow: '0 8px 30px rgba(168, 85, 247, 0.5)' }}
                                  >
                                    <TrendingUp className="w-6 h-6 text-white" />
                                  </motion.div>
                                  <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-fuchsia-400">
                                    Long-term Development
                                  </h3>
                                </div>
                                <div className="space-y-3">
                                  {nextSteps.longTerm.map((step, idx) => (
                                    <motion.div
                                      key={idx}
                                      initial={{ opacity: 0, x: -20 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ delay: 0.4 + idx * 0.08 }}
                                      whileHover={{ scale: 1.02, x: 5 }}
                                      className="group relative flex items-start gap-4 p-5 bg-gradient-to-r from-purple-500/20 via-violet-500/15 to-fuchsia-500/20 rounded-xl border-2 border-purple-500/40 hover:border-purple-400/70 transition-all cursor-default overflow-hidden"
                                      style={{ boxShadow: '0 4px 20px rgba(168, 85, 247, 0.15)' }}
                                    >
                                      {/* Animated background shimmer */}
                                      <motion.div
                                        className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-400/10 to-transparent"
                                        animate={{ x: ['-100%', '200%'] }}
                                        transition={{ duration: 3, repeat: Infinity, repeatDelay: 2, ease: "easeInOut" }}
                                      />
                                      <div className="relative z-10 w-6 h-6 bg-gradient-to-br from-purple-400 to-fuchsia-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-lg group-hover:shadow-purple-400/50 transition-shadow">
                                        <CheckCircle className="w-4 h-4 text-white" />
                                      </div>
                                      <MarkdownText className="relative z-10 text-slate-100 group-hover:text-white transition-colors leading-relaxed">
                                        {step}
                                      </MarkdownText>
                                    </motion.div>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </div>
                        );
                      }

                      // Handle object structure with recommendations (Module 01 & 02 DDD format)
                      if (nextSteps && typeof nextSteps === 'object' && !Array.isArray(nextSteps) && nextSteps.recommendations) {
                        return (
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                          >
                            {nextSteps.description && (
                              <div className="p-4 bg-gradient-to-r from-indigo-500/15 via-purple-500/15 to-pink-500/15 border-l-4 border-indigo-500 rounded-r-xl mb-6">
                                <p className="text-slate-200 text-lg leading-relaxed">{nextSteps.description}</p>
                              </div>
                            )}
                            <div className="space-y-3">
                              {nextSteps.recommendations.map((step, idx) => (
                                <motion.div
                                  key={idx}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.1 + idx * 0.08 }}
                                  whileHover={{ scale: 1.02, x: 5 }}
                                  className="group relative flex items-start gap-4 p-5 bg-gradient-to-r from-conversation-teal/20 via-cyan-500/15 to-bright-gold/20 rounded-xl border-2 border-conversation-teal/40 hover:border-conversation-teal/70 transition-all cursor-default overflow-hidden"
                                  style={{ boxShadow: '0 4px 20px rgba(20, 184, 166, 0.15)' }}
                                >
                                  {/* Animated background shimmer */}
                                  <motion.div
                                    className="absolute inset-0 bg-gradient-to-r from-transparent via-conversation-teal/10 to-transparent"
                                    animate={{ x: ['-100%', '200%'] }}
                                    transition={{ duration: 3, repeat: Infinity, repeatDelay: 2, ease: "easeInOut" }}
                                  />
                                  <div className="relative z-10 w-6 h-6 bg-gradient-to-br from-conversation-teal to-bright-gold rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-lg group-hover:shadow-conversation-teal/50 transition-shadow">
                                    <CheckCircle className="w-4 h-4 text-white" />
                                  </div>
                                  <MarkdownText className="relative z-10 text-slate-100 group-hover:text-white transition-colors leading-relaxed">
                                    {step}
                                  </MarkdownText>
                                </motion.div>
                              ))}
                            </div>
                          </motion.div>
                        );
                      }

                      // Handle array structure (older modules)
                      if (Array.isArray(nextSteps)) {
                        return (
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                          >
                            <div className="space-y-3">
                              {nextSteps.map((step, idx) => (
                                <motion.div
                                  key={idx}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.1 + idx * 0.08 }}
                                  whileHover={{ scale: 1.02, x: 5 }}
                                  className="group relative flex items-start gap-4 p-5 bg-gradient-to-r from-conversation-teal/20 via-cyan-500/15 to-bright-gold/20 rounded-xl border-2 border-conversation-teal/40 hover:border-conversation-teal/70 transition-all cursor-default overflow-hidden"
                                  style={{ boxShadow: '0 4px 20px rgba(20, 184, 166, 0.15)' }}
                                >
                                  {/* Animated background shimmer */}
                                  <motion.div
                                    className="absolute inset-0 bg-gradient-to-r from-transparent via-conversation-teal/10 to-transparent"
                                    animate={{ x: ['-100%', '200%'] }}
                                    transition={{ duration: 3, repeat: Infinity, repeatDelay: 2, ease: "easeInOut" }}
                                  />
                                  <div className="relative z-10 w-6 h-6 bg-gradient-to-br from-conversation-teal to-bright-gold rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-lg group-hover:shadow-conversation-teal/50 transition-shadow">
                                    <CheckCircle className="w-4 h-4 text-white" />
                                  </div>
                                  <MarkdownText className="relative z-10 text-slate-100 group-hover:text-white transition-colors leading-relaxed">
                                    {step}
                                  </MarkdownText>
                                </motion.div>
                              ))}
                            </div>
                          </motion.div>
                        );
                      }

                      return null;
                    })()}
                  </div>
                )}

              </Card>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Bottom Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="relative z-10 mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4"
        >
          <Button
            variant="ghost"
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            className="flex items-center justify-center gap-2 order-2 sm:order-1"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Previous</span>
            <span className="sm:hidden">Back</span>
          </Button>

          <Button
            onClick={handleContinue}
            disabled={currentPage === pages.length - 1 && moduleIsCompleted}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-conversation-teal to-bright-gold text-white px-6 sm:px-8 py-3 text-sm sm:text-base font-semibold hover:shadow-lg hover:shadow-conversation-teal/30 transition-all order-1 sm:order-2"
          >
            {currentPage === pages.length - 1 ? (
              <>
                <Award className="w-4 h-4 sm:w-5 sm:h-5" />
                Complete Module
              </>
            ) : (
              <>
                <span className="hidden sm:inline">Mark as Complete & Continue</span>
                <span className="sm:hidden">Continue</span>
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </>
            )}
          </Button>
        </motion.div>

        {/* Celebration Modal */}
        <CelebrationModal
          show={showCelebration}
          onClose={() => setShowCelebration(false)}
          message={celebrationMessage}
        />

        {/* Completion Certificate */}
        <CompletionCertificate
          show={showCertificate}
          onClose={() => {
            setShowCertificate(false);
            onComplete?.();
          }}
          module={module}
        />

        {/* Course Mastery Certificate */}
        <CourseMasteryCertificate
          show={showCourseMastery}
          onClose={() => {
            setShowCourseMastery(false);
            onComplete?.();
          }}
          completedModules={useGamificationStore.getState().completedModules}
          totalPoints={useGamificationStore.getState().totalPoints}
        />
      </div>
    </div>
  );
};

export default PhilosophyModuleDetail;